import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import "bootstrap/dist/css/bootstrap.min.css";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";

const AddSiBill = () => {
  const { state } = useLocation();
  const rowData = state?.rowData || {};
  const isEditMode = state?.editMode || false;
  const navigate = useNavigate();
  const [isVerified, setIsVerified] = useState(false);
  const [bills, setBills] = useState([
    { billType: "Bill", billNo: "", billDate: "" },
  ]);
  const [showParchiAlert, setShowParchiAlert] = useState(false);
  const token = localStorage.getItem("token");

  // Fetch existing bills if edit mode
  useEffect(() => {
    if (isEditMode && rowData.id) {
      fetchExistingBills(rowData.id);
    }
  }, [isEditMode, rowData.id]);

  const fetchExistingBills = async (siId) => {
    try {
      const response = await apiClient.get(SERVER_BASE + "/getSiBillDetails", {
        params: { siId },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data && response.data.length > 0) {
        const formattedBills = response.data.map((b) => ({
          billType: b.billType || "Bill",
          billNo: b.billNo || "",
          billDate: b.billDate ? moment(b.billDate).format("YYYY-MM-DD") : "",
        }));
        setBills(formattedBills);
        setShowParchiAlert(formattedBills.some((b) => b.billType === "Parchi / Paper"));
      }
    } catch (error) {
      console.error("Error fetching previous bills:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch previous bills.",
      });
    }
  };

  const handleBillChange = (index, field, value) => {
    const newBills = [...bills];
    newBills[index][field] = value;
    setBills(newBills);

    // Show alert if any bill type is Parchi / Paper
    if (field === "billType") {
      const hasParchi = newBills.some((b) => b.billType === "Parchi / Paper");
      setShowParchiAlert(hasParchi);
    }
  };

  const addRow = () =>
    setBills([...bills, { billType: "Bill", billNo: "", billDate: "" }]);

  const removeRow = (index) => setBills(bills.filter((_, i) => i !== index));

  const handleSave = async () => {
    if (showParchiAlert) return; // Don't save if any parchi is selected

    // Validation: all rows must have Bill No and Bill Date
    for (let i = 0; i < bills.length; i++) {
      if (!bills[i].billNo || !bills[i].billDate) {
        Swal.fire({
          icon: "warning",
          text: `Please enter Bill No and Bill Date for row ${i + 1}`,
          toast: true,
          showConfirmButton: false,
          timer: 3000,
          position: "top-end",
        });
        return;
      }
    }

    try {
      const payload = bills.map((b) => ({
        siId: rowData.id,
        siCode: rowData.siCode,
        billType: b.billType,
        billNo: b.billNo,
        billDate: b.billDate,
      }));

      const endpoint = isEditMode
        ? "/updateSiBillDetails"
        : "/addSiBillDetails";

      await apiClient.post(SERVER_BASE + endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      Swal.fire({
        icon: "success",
        title: "Success",
        text: isEditMode
          ? "Bills updated successfully!"
          : "Bills added successfully!",
      });

      navigate(-1);
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: isEditMode ? "Failed to update bills." : "Failed to add bills.",
      });
    }
  };

  return (
    <div className="formcontainer mt-5">
      <h4>
        {isEditMode ? "Edit Bills" : "Add Bills"} for SI: {rowData.siCode}
      </h4>

      {/* SecurityIn details */}
      <div className="card p-3 mb-3">
        <div className="row">
          <div className="col-md-3">
            <b>Vehicle Entry:</b> {rowData.vehicleNumber || "-"}
          </div>
          <div className="col-md-3">
            <b>Entry Date:</b>{" "}
            {rowData.entryDate
              ? moment(rowData.entryDate).format("DD-MMM-YY")
              : "-"}
          </div>
          <div className="col-md-3">
            <b>Entry Time:</b> {rowData.entryTime || "-"}
          </div>
          <div className="col-md-3">
            <b>SR No:</b> {rowData.gsrs || "-"}
          </div>
        </div>
        <div className="row mt-2">
          <div className="col-md-3">
            <b>Came From:</b> {rowData.cameFrom || "-"}
          </div>
          <div className="col-md-4">
            <b>Gate Entry By:</b> {rowData.sewadarName} - {rowData.badgeId}
          </div>
        </div>
      </div>

      {/* Bill input label row */}
      <div className="row mb-2 fw-bold">
        <div className="col-md-1">#</div>
        <div className="col-md-2">Document Type</div>
        <div className="col-md-3">Document No</div>
        <div className="col-md-3">Document Date</div>
        <div className="col-md-2">Action</div>
      </div>

      {bills.map((b, i) => (
        <div key={i} className="row mb-2 align-items-center">
          <div className="col-md-1">
            <input
              type="text"
              className="form-control"
              value={i + 1}
              readOnly
            />
          </div>
          <div className="col-md-2">
            <select
              className="form-select"
              value={b.billType}
              onChange={(e) =>
                handleBillChange(i, "billType", e.target.value)
              }
            >
              <option value="Bill">Bill</option>
              <option value="Challan">Challan</option>
              <option value="Inward Memo">Inward Memo</option>
              <option value="Parchi / Paper">Parchi / Paper</option>
            </select>
          </div>
          <div className="col-md-3">
            <input
              type="text"
              className="form-control"
              placeholder="Bill No"
              value={b.billNo}
              onChange={(e) =>
                handleBillChange(i, "billNo", e.target.value)
              }
            />
          </div>
          <div className="col-md-3">
            <input
              type="date"
              className="form-control"
              value={b.billDate}
              onChange={(e) =>
                handleBillChange(i, "billDate", e.target.value)
              }
            />
          </div>
          <div className="col-md-2 d-flex">
            <button className="btn btn-success btn-sm me-1" onClick={addRow}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
            {bills.length > 1 && (
              <button
                className="btn btn-danger btn-sm"
                onClick={() => removeRow(i)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
          </div>
        </div>
      ))}

      {/* Inline alert for Parchi / Paper */}
      {showParchiAlert && (
        <div className="alert alert-secondary mt-3" role="alert">
          पर्ची का विवरण मान्य नहीं है। कृपया बिलों की प्राप्ति के समय सभी बिलों का विवरण एक साथ दर्ज करें।
        </div>
      )}

      {/* Verification Checkbox */}
      <div className="form-check mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          id="verifyBills"
          checked={isVerified}
          onChange={(e) => setIsVerified(e.target.checked)}
          disabled={showParchiAlert}
        />
        <label className="form-check-label" htmlFor="verifyBills">
          संपूर्ण बिलों का भौतिक सत्यापन उनकी क्रमांक तथा तिथि से पूर्ण कर लिया गया है
        </label>
      </div>

      {/* Save & Cancel buttons */}
      <div className="mt-3">
        <button
          className="btn btn-success me-2"
          onClick={handleSave}
          disabled={!isVerified || showParchiAlert}
        >
          {isEditMode ? "Update" : "Save"}
        </button>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AddSiBill;
